import { createAction, createSlice } from '@reduxjs/toolkit';

import { IApiError, IRowsAndTotal } from '../../api/types';
import { ILocation } from '../../api/locations/types';
import { EStationStatus } from '../../api/station/types';
import {
    DEFAULT_MAX_POWER,
    DEFAULT_MIN_POWER,
} from '../../app/main/components/filters/power/power.constants';

export interface ILocationsListState {
    entry: IRowsAndTotal<ILocation>;
    loading: boolean;
    reload: boolean;
    cursor: number;
    error: IApiError;
}

export interface ILocationsListParamsState {
    search: string;
    connectorStatus?: EStationStatus;
    isConnectorAvailable?: boolean;
    isFree?: boolean;
    isPaid?: boolean;
    maxPower: number;
    canadianMadeOnly?: boolean;
    connectorTypes: string[];
    minPower: number;
}

const initialState: ILocationsListState = {
    entry: {
        rows: [],
        data: [],
        total: 0,
    },
    loading: true,
    reload: true,
    cursor: 0,
    error: <IApiError>{},
};

const searchInitialState: ILocationsListParamsState = {
    search: '',
    connectorStatus: undefined,
    isConnectorAvailable: undefined,
    isFree: undefined,
    isPaid: undefined,
    maxPower: DEFAULT_MAX_POWER,
    canadianMadeOnly: undefined,
    connectorTypes: [],
    minPower: DEFAULT_MIN_POWER,
};

export const setLocationsParamsSearch = createAction<string>('setLocationsParamsSearch');
export const setLocationsParamsConnectorStatus = createAction<EStationStatus>(
    'setLocationsParamsConnectorStatus'
);
export const setLocationsParamsIsConnectorAvailable = createAction<boolean>(
    'setLocationsParamsIsConnectorAvailable'
);
export const setLocationsParamsIsFree = createAction<boolean>('setLocationsParamsIsFree');
export const setLocationsParamsIsPaid = createAction<boolean>('setLocationsParamsIsPaid');
export const setLocationsParamsIsAvailable = createAction<boolean>('setLocationsParamsIsAvailable');
export const setLocationsParamsPower = createAction<{ maxPower?: number; minPower?: number }>(
    'setLocationsParamsPower'
);
export const setLocationsParamsCanadianMadeOnly = createAction<boolean>(
    'setLocationsParamsCanadianMadeOnly'
);
export const setLocationsParamsConnectorTypes = createAction<string[]>(
    'setLocationsParamsConnectorTypes'
);
export const resetLocationsParams = createAction('resetLocationsParams');

const locationsListParamsSlice = createSlice({
    name: 'locationsListParams',
    initialState: searchInitialState,
    reducers: {},
    extraReducers: builder => {
        builder.addCase(setLocationsParamsSearch, (state, { payload }) => {
            state.search = payload;
        });
        builder.addCase(setLocationsParamsConnectorStatus, (state, { payload }) => {
            state.connectorStatus = payload;
        });
        builder.addCase(setLocationsParamsIsFree, (state, { payload }) => {
            state.isFree = payload;
        });
        builder.addCase(setLocationsParamsIsPaid, (state, { payload }) => {
            state.isPaid = payload;
        });
        builder.addCase(setLocationsParamsIsAvailable, (state, { payload }) => {
            // state.connectorStatus = payload ? EStationStatus.AVAILABLE : undefined;
            state.isConnectorAvailable = payload || undefined;
        });
        builder.addCase(setLocationsParamsPower, (state, { payload }) => {
            if (payload.maxPower) {
                state.maxPower = payload.maxPower;
            }
            if (payload.minPower) {
                state.minPower = payload.minPower;
            }
        });
        builder.addCase(setLocationsParamsCanadianMadeOnly, (state, { payload }) => {
            state.canadianMadeOnly = payload;
        });
        builder.addCase(setLocationsParamsConnectorTypes, (state, { payload }) => {
            state.connectorTypes = payload;
        });
        builder.addCase(resetLocationsParams, state => {
            state = {
                ...searchInitialState,
                search: state.search,
            };
            return state;
        });
    },
});

export const locationsListParamsReducer = locationsListParamsSlice.reducer;
